import axios from 'axios';
import { HubTokenExchangeResponse, LegacySession, Role } from '../interfaces/auth';
import { StorageName } from '../util/constants';
import { computeCodeChallenge, generateCodeVerifier, generateState } from '../utils/pkce';
import { AuthService } from './auth';
import MixpanelService from './mixpanel';
import { identifyMonitoredUser, reportMonitoredError, reportSessionEvent } from './sentry';
import { sessionMonitor } from './session';

export const HUB_CALLBACK_PATH = '/auth/callback';
const VERIFIER_KEY = 'hubSignIn.codeVerifier';
const STATE_KEY = 'hubSignIn.state';
const ERROR_KEY = 'hubSignIn.error';

export const HUB_AUTH_SOURCE = 'hub';

export const isHubSignInEnabled = (): boolean =>
  process.env.REACT_APP_HUB_SIGNIN_ENABLED === 'true';

const DEFAULT_APPLICATION_SLUG = 'ops-dashboard';

const applicationSlug = (): string =>
  process.env.REACT_APP_HUB_APPLICATION_SLUG || DEFAULT_APPLICATION_SLUG;

const redirectUri = (): string => `${window.location.origin}${HUB_CALLBACK_PATH}`;

export type HubSignInErrorCode =
  | 'denied'
  | 'state_mismatch'
  | 'expired'
  | 'network'
  | 'unavailable';

const ERROR_MESSAGES: Record<HubSignInErrorCode, string> = {
  denied: 'Sign-in with Enara Hub was cancelled or denied. Please try again.',
  state_mismatch: 'The sign-in response could not be verified. Please try again.',
  expired: 'The sign-in link expired or was already used. Please try again.',
  network: 'Could not reach Enara Hub. Check your connection and try again.',
  unavailable: 'Hub sign-in is not available for this dashboard yet.',
};

export class HubSignInError extends Error {
  constructor(
    public readonly code: HubSignInErrorCode,
    public readonly status?: number,
  ) {
    super(ERROR_MESSAGES[code]);
    this.name = 'HubSignInError';
  }
}

export const startHubSignIn = async (): Promise<void> => {
  const signInUrl = process.env.REACT_APP_HUB_SIGNIN_URL;
  if (!signInUrl || !process.env.REACT_APP_HUB_AUTH_API_URL) {
    throw new Error(
      'Hub sign-in is enabled but REACT_APP_HUB_SIGNIN_URL or REACT_APP_HUB_AUTH_API_URL is not set',
    );
  }

  const verifier = generateCodeVerifier();
  const state = generateState();
  const challenge = await computeCodeChallenge(verifier);
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);

  const url = new URL(signInUrl);
  url.searchParams.set('applicationSlug', applicationSlug());
  url.searchParams.set('redirect_uri', redirectUri());
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  url.searchParams.set('state', state);
  window.location.assign(url.toString());
};

// Core has no endpoint that returns the roles for a token (they only come back from
// POST /v1/auth), so they must arrive with the Hub-minted session.
const isRole = (role: unknown): role is Role =>
  typeof (role as Role | null)?.name === 'string' && (role as Role).id != null;

export const requireLegacyRoles = (session: LegacySession): Role[] => {
  const { roles } = session;
  if (!Array.isArray(roles) || roles.length === 0 || !roles.every(isRole)) {
    throw new HubSignInError('unavailable');
  }
  return roles;
};

const isLegacySession = (value: unknown): value is LegacySession => {
  const session = value as LegacySession | undefined;
  const userId = session?.coreUserId;
  return (
    typeof session?.authToken === 'string' &&
    session.authToken.length > 0 &&
    ((typeof userId === 'number' && Number.isFinite(userId)) ||
      (typeof userId === 'string' && userId.length > 0))
  );
};

const exchangeCode = async (code: string, verifier: string) => {
  try {
    const response = await axios.post<HubTokenExchangeResponse>(
      `${process.env.REACT_APP_HUB_AUTH_API_URL}/auth/token`,
      {
        grant_type: 'authorization_code',
        code,
        redirect_uri: redirectUri(),
        code_verifier: verifier,
        application_slug: applicationSlug(),
      },
    );
    return response.data;
  } catch (error) {
    const status = (error as { response?: { status?: number } }).response?.status;
    if (status === undefined) throw new HubSignInError('network');
    if (status === 400) throw new HubSignInError('expired', status);
    throw new HubSignInError('unavailable', status);
  }
};

const establishSession = async (session: LegacySession, roles: Role[]): Promise<void> => {
  // A failed profile fetch must not leave the previous user's identity behind
  localStorage.removeItem(StorageName.PROFILE);
  AuthService.setRoles(roles);
  // Marked as Hub-minted, so sign-out also ends it on the Hub
  sessionMonitor.startSession({
    auth: { user_id: String(session.coreUserId), auth_token: session.authToken, roles },
    expiresAt: session.expiresAt,
    source: HUB_AUTH_SOURCE,
  });

  let email = '';
  try {
    const profile = await AuthService.getProfile(session.authToken);
    AuthService.setProfile(profile);
    email = profile.user.email;
  } catch (e) {
    reportMonitoredError({ error: e });
  }
  identifyMonitoredUser(Number(session.coreUserId), email);
  MixpanelService.track('Login');
};

const REPORTED_CODES: HubSignInErrorCode[] = ['unavailable', 'network'];

const reportHubSignInError = (error: unknown): void => {
  if (!(error instanceof HubSignInError) || !REPORTED_CODES.includes(error.code)) return;
  reportMonitoredError({
    error,
    tags: { hub_signin_code: error.code, hub_signin_status: String(error.status ?? 'none') },
  });
};

const runHubSignIn = async (search: string): Promise<void> => {
  const params = new URLSearchParams(search);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  const expectedState = sessionStorage.getItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);

  const code = params.get('code');
  if (params.get('error') || !code) throw new HubSignInError('denied');
  if (!verifier || !expectedState || params.get('state') !== expectedState) {
    throw new HubSignInError('state_mismatch');
  }

  const body = await exchangeCode(code, verifier);
  const session = body?.data?.legacySession;
  if (!isLegacySession(session)) throw new HubSignInError('unavailable');

  const roles = requireLegacyRoles(session);
  await establishSession(session, roles);
};

export const completeHubSignIn = async (search: string): Promise<void> => {
  try {
    await runHubSignIn(search);
  } catch (error) {
    reportHubSignInError(error);
    throw error;
  }
};

const reportHubSignOutFailure = (reason: string, detail: { status?: number; code?: string }) => {
  try {
    reportSessionEvent('Hub sign-out failed', { reason, host: 'hub', ...detail });
  } catch (e) {
    // Reporting must not break the sign-out
  }
};

/**
 * Ends a Hub-minted session on the Hub, which also ends it in Core. Fire-and-forget:
 * `keepalive` lets the request outlive the navigation to the login page, and a failure
 * must never hold up the local sign-out. The Hub answers 204 whatever the outcome, so
 * anything else is reported, with no token, URL or body. Best-effort: a report sent as
 * the page unloads may be lost.
 *
 * The returned promise never rejects; callers need not wait for it.
 */
export const signOutHubSession = (authToken: string): Promise<void> => {
  const apiUrl = process.env.REACT_APP_HUB_AUTH_API_URL;
  if (!apiUrl) {
    reportHubSignOutFailure('hub-signout-unconfigured', {});
    return Promise.resolve();
  }
  try {
    return (
      fetch(`${apiUrl}/auth/legacy-session/logout`, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ applicationSlug: applicationSlug(), authToken }),
      })
        .then((response) => {
          if (!response.ok) {
            reportHubSignOutFailure('hub-signout-rejected', { status: response.status });
          }
        })
        // A CORS rejection and a network failure are indistinguishable from here
        .catch(() => reportHubSignOutFailure('hub-signout-network', { code: 'network-or-cors' }))
    );
  } catch (e) {
    // fetch can throw synchronously (e.g. a keepalive body over the browser limit)
    reportHubSignOutFailure('hub-signout-threw', {});
    return Promise.resolve();
  }
};

export const setHubSignInError = (message: string): void => {
  sessionStorage.setItem(ERROR_KEY, message);
};

export const peekHubSignInError = (): string | null => sessionStorage.getItem(ERROR_KEY);

export const clearHubSignInError = (): void => {
  sessionStorage.removeItem(ERROR_KEY);
};
